import { useTodayInbox } from '@/hooks/useTodayInbox';
import { BELL_LINK, OPEN_BELL_EVENT, clearRows, rowDetail, waitingRows, type TodayRow } from '@/lib/todayInbox';
import jobStyles from '../partner-jobs/PartnerJobs.module.css';
import styles from './TodayPage.module.css';

/**
 * Today (Warren, 21 October 2026, item M: "Today inbox with counts"): the Super Admin's landing after sign-in. One
 * screen of what is waiting, each row a count and the screen that clears it, oldest first, with what is past EVI's one
 * business day promise flagged. The Api decides every row and who may see it (GET /admin/today); a row the caller's
 * permissions do not open never arrives.
 */
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function TodayPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const q = useTodayInbox(true);
  const open = (row: TodayRow) => {
    if (row.link === BELL_LINK) window.dispatchEvent(new Event(OPEN_BELL_EVENT));
    else onNavigate(row.link);
  };
  const heading = <div className={jobStyles.heading}><div>
    <h1 className={jobStyles.title}>Today</h1>
    <p className={jobStyles.lead}>What is waiting for you, oldest first. Each row opens the screen that clears it. Every EVI step promises one
      business day (Monday to Friday, US federal holidays excluded, in ET). Press <kbd className={styles.kbd}>{isMac ? 'Cmd' : 'Ctrl'}</kbd>{' '}
      <kbd className={styles.kbd}>K</kbd> to jump to any page.</p>
  </div></div>;
  if (q.error) return <main className={jobStyles.page}>{heading}<p role="alert" className={jobStyles.message}>Today is unavailable. Refresh to try again.</p></main>;
  if (!q.data) return <main className={jobStyles.page}>{heading}<p className={jobStyles.message}>Loading what is waiting.</p></main>;
  const rows = waitingRows(q.data.rows), clear = clearRows(q.data.rows);
  const late = rows.reduce((n, r) => n + (r.in_total ? r.overdue : 0), 0);
  return <main className={jobStyles.page}>
    {heading}
    <section className={jobStyles.card} aria-labelledby="today-waiting">
      <div className={jobStyles.cardHead}>
        <h2 id="today-waiting" className={jobStyles.kicker}>Waiting</h2>
        <span className={jobStyles.slotTag} data-tone={late ? 'warn' : q.data.total ? undefined : 'good'}>
          {q.data.total ? `${q.data.total} waiting${late ? `, ${late} past the promise` : ''}` : 'Nothing waiting'}
        </span>
      </div>
      {rows.length ? <ul className={styles.rows}>
        {rows.map(row => <li key={row.key} className={styles.row}>
          <span className={styles.count} data-tone={row.overdue ? 'warn' : undefined}>{row.count}</span>
          <span className={styles.what}>
            <span className={styles.label}>{row.label}</span>
            <span className={styles.detail}>{row.overdue ? <span className={styles.late}>Overdue. </span> : null}{rowDetail(row)}</span>
          </span>
          <button type="button" className={styles.open} onClick={() => open(row)}>{row.link === BELL_LINK ? 'Open the bell' : 'Open'}</button>
        </li>)}
      </ul> : <p className={jobStyles.hint}>Nothing is waiting for you.</p>}
      {clear.length > 0 && <p className={jobStyles.hint}>Clear: {clear.join(', ')}.</p>}
    </section>
  </main>;
}
